/**
 * Zod schemas for physics configuration
 * Following CLAUDE.md rules: NO defaults in schemas, separate validation from defaults
 */

import { z } from "zod";

/**
 * Schema for physics shape types
 */
export const PhysicsShapeTypeSchema = z.enum(["BOX", "SPHERE", "CONVEX_HULL"]);

/**
 * Schema for 3D vector (gravity, etc.)
 */
export const Vector3Schema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number(),
});

/**
 * Schema for physics scene options
 */
export const PhysicsOptionsSchema = z.object({
  enableCollisionCallbacks: z.boolean().optional(),
  gravity: Vector3Schema.optional(),
});

/**
 * Schema for physics body options
 */
export const PhysicsBodyOptionsSchema = z.object({
  angularDamping: z.number().min(0).optional(),
  /** Offset shape center from mesh origin. Useful for "thick floor" where top surface is at Y=0. */
  center: Vector3Schema.optional(),
  /** Explicit BOX dimensions (width, height, depth). Only applies when shapeType="BOX". */
  extents: Vector3Schema.optional(),
  friction: z.number().min(0).max(1).optional(),
  linearDamping: z.number().min(0).optional(),
  mass: z.number().min(0),
  restitution: z.number().min(0).max(1).optional(),
  shapeType: PhysicsShapeTypeSchema,
});

/**
 * Inferred types from schemas
 */
export type PhysicsShapeTypeSchema = z.infer<typeof PhysicsShapeTypeSchema>;
export type Vector3SchemaType = z.infer<typeof Vector3Schema>;
export type PhysicsOptionsSchemaType = z.infer<typeof PhysicsOptionsSchema>;
export type PhysicsBodyOptionsSchemaType = z.infer<
  typeof PhysicsBodyOptionsSchema
>;

/**
 * Default physics options (separate from schema per CLAUDE.md)
 */
export const defaultPhysicsOptions: PhysicsOptionsSchemaType = {
  enableCollisionCallbacks: true,
  gravity: { x: 0, y: -9.8, z: 0 },
};

/**
 * Default physics body options (separate from schema per CLAUDE.md)
 */
export const defaultPhysicsBodyOptions: Partial<PhysicsBodyOptionsSchemaType> =
  {
    angularDamping: 0.1,
    friction: 0.5,
    linearDamping: 0.1,
    restitution: 0.3,
  };

/**
 * Validate physics options
 */
export function validatePhysicsOptions(
  input: unknown,
): PhysicsOptionsSchemaType {
  return PhysicsOptionsSchema.parse(input);
}

/**
 * Safely validate physics options
 */
export function safeValidatePhysicsOptions(
  input: unknown,
):
  | { success: true; data: PhysicsOptionsSchemaType }
  | { success: false; error: z.ZodError } {
  const result = PhysicsOptionsSchema.safeParse(input);
  if (result.success) {
    return { data: result.data, success: true };
  }
  return { error: result.error, success: false };
}

/**
 * Validate physics body options
 */
export function validatePhysicsBodyOptions(
  input: unknown,
): PhysicsBodyOptionsSchemaType {
  return PhysicsBodyOptionsSchema.parse(input);
}

/**
 * Safely validate physics body options
 */
export function safeValidatePhysicsBodyOptions(
  input: unknown,
):
  | { success: true; data: PhysicsBodyOptionsSchemaType }
  | { success: false; error: z.ZodError } {
  const result = PhysicsBodyOptionsSchema.safeParse(input);
  if (result.success) {
    return { data: result.data, success: true };
  }
  return { error: result.error, success: false };
}
